import { useEffect, useMemo, useRef, useState } from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { MapPinned } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Kartgrunnlag: OpenFreeMap «Positron» – lyse, dempa vektorkart utan API-nøkkel og utan grenser.
 * Attribusjon er påkravd (OpenFreeMap / OpenMapTiles / OpenStreetMap).
 */
export const STYLE_URL = "https://tiles.openfreemap.org/styles/positron";
export const ATTRIBUTION_HTML =
  '<a href="https://openfreemap.org" target="_blank" rel="noopener noreferrer">OpenFreeMap</a> © <a href="https://www.openmaptiles.org/" target="_blank" rel="noopener noreferrer">OpenMapTiles</a> © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>';
const BRAND_RED = "#ef3120";

export const hasCoords = (p) => p && typeof p.lat === "number" && typeof p.lng === "number";

export const pinElement = (label, tone = "neutral", size = 28) => {
  const el = document.createElement("span");
  el.className = `ek-pin ek-pin-${tone}`;
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.fontSize = size <= 16 ? "0" : "11px";
  el.textContent = label ?? "";
  return el;
};

/** Opprettar eit MapLibre-kart i containeren og returnerer instansen når stilen er lasta. */
export const useMapLibre = (containerRef, { interactive = true, enabled = true, center = [5.9, 60.1], zoom = 8 } = {}) => {
  const [map, setMap] = useState(null);
  useEffect(() => {
    if (!enabled || !containerRef.current) return undefined;
    const m = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE_URL,
      center,
      zoom,
      attributionControl: false,
      interactive,
      scrollZoom: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
      cooperativeGestures: interactive,
      fadeDuration: 0,
    });
    m.touchZoomRotate?.disableRotation();
    const onLoad = () => setMap(m);
    m.on("load", onLoad);
    const ro = new ResizeObserver(() => m.resize());
    ro.observe(containerRef.current);
    return () => {
      ro.disconnect();
      m.off("load", onLoad);
      setMap(null);
      m.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive, enabled]);
  return map;
};

/** Tilpass utsnittet til punkta (lat/lng-par). */
export const fitTo = (map, points, { padding = 40, maxZoom = 15 } = {}) => {
  if (!map || !points.length) return;
  if (points.length === 1) {
    map.jumpTo({ center: [points[0][1], points[0][0]], zoom: Math.min(13, maxZoom) });
    return;
  }
  const b = new maplibregl.LngLatBounds();
  points.forEach((p) => b.extend([p[1], p[0]]));
  map.fitBounds(b, { padding, maxZoom, duration: 0 });
};

const ROUTE_SOURCE_ID = "ek-route";

const setRouteLine = (map, line, dashed) => {
  const data = { type: "Feature", geometry: { type: "LineString", coordinates: line.map((p) => [p[1], p[0]]) }, properties: {} };
  if (map.getSource(ROUTE_SOURCE_ID)) {
    map.getSource(ROUTE_SOURCE_ID).setData(data);
  } else {
    map.addSource(ROUTE_SOURCE_ID, { type: "geojson", data });
    map.addLayer({ id: `${ROUTE_SOURCE_ID}-casing`, type: "line", source: ROUTE_SOURCE_ID, layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#ffffff", "line-width": 7, "line-opacity": 0.95 } });
    map.addLayer({ id: `${ROUTE_SOURCE_ID}-line`, type: "line", source: ROUTE_SOURCE_ID, layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": BRAND_RED, "line-width": 3.5 } });
  }
  map.setPaintProperty(`${ROUTE_SOURCE_ID}-line`, "line-dasharray", dashed ? [0.5, 2.2] : [1, 0]);
  map.setLayoutProperty(`${ROUTE_SOURCE_ID}-line`, "line-cap", dashed ? "round" : "round");
};

const clearRouteLine = (map) => {
  if (map.getLayer(`${ROUTE_SOURCE_ID}-line`)) map.removeLayer(`${ROUTE_SOURCE_ID}-line`);
  if (map.getLayer(`${ROUTE_SOURCE_ID}-casing`)) map.removeLayer(`${ROUTE_SOURCE_ID}-casing`);
  if (map.getSource(ROUTE_SOURCE_ID)) map.removeSource(ROUTE_SOURCE_ID);
};

export const Attribution = () => (
  <div
    className="pointer-events-none absolute bottom-1 right-1.5 z-10 rounded bg-card px-1.5 py-0.5 text-[9px] leading-3 text-muted-foreground [&_a]:pointer-events-auto [&_a:hover]:underline"
    dangerouslySetInnerHTML={{ __html: ATTRIBUTION_HTML }}
  />
);

/**
 * Rutekart: A → stopp → B med rutelinje.
 * markers = [{ lat, lng, label, tone, title }], geometry = [[lat, lng], ...] (Google/OSRM) eller null (luftlinje, stipla).
 */
export const RouteMap = ({ markers = [], geometry = null, source = null, className, heightClassName = "h-56 md:h-72", emptyText = "Vel adresser for å sjå kartet.", interactive = true, testId = "route-map" }) => {
  const containerRef = useRef(null);
  const markerRefs = useRef([]);
  const valid = useMemo(() => markers.filter(hasCoords), [markers]);
  const line = useMemo(() => {
    if (geometry && geometry.length > 1) return geometry;
    if (valid.length > 1) return valid.map((m) => [m.lat, m.lng]);
    return [];
  }, [geometry, valid]);
  const dashed = !geometry || geometry.length < 2 || source === "estimat" || source === "manuell";
  const hasAny = valid.length > 0;
  const map = useMapLibre(containerRef, { interactive, enabled: hasAny });

  const markersKey = JSON.stringify(valid);
  const lineKey = JSON.stringify(line);
  useEffect(() => {
    if (!map) return;
    markerRefs.current.forEach((m) => m.remove());
    markerRefs.current = valid.map((m) => {
      const el = pinElement(m.label, m.tone);
      if (m.title) el.title = m.title;
      return new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat([m.lng, m.lat]).addTo(map);
    });
    if (line.length > 1) setRouteLine(map, line, dashed);
    else clearRouteLine(map);
    fitTo(map, [...valid.map((m) => [m.lat, m.lng]), ...line]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, markersKey, lineKey, dashed]);

  if (!hasAny) {
    return (
      <div className={cn("flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-background text-center", heightClassName, className)} data-testid={`${testId}-empty`}>
        <MapPinned className="mb-2 h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      </div>
    );
  }
  return (
    <div className={cn("relative overflow-hidden rounded-lg border border-border bg-muted", heightClassName, className)} data-testid={testId}>
      <div ref={containerRef} className="h-full w-full" />
      <Attribution />
    </div>
  );
};

/** Byggjer markørar frå logistikk-objektet på ei ordre. */
export const markersFromLogistics = (lg) => {
  if (!lg) return [];
  const letters = "ABCDEFGHIJ";
  const out = [];
  if (hasCoords(lg.pickup)) out.push({ lat: lg.pickup.lat, lng: lg.pickup.lng, label: "A", tone: "dark", title: lg.pickup.name || lg.pickup.address });
  (lg.stops || []).filter(hasCoords).forEach((s, i) => out.push({ lat: s.lat, lng: s.lng, label: letters[i + 1], tone: "neutral", title: s.name || s.address }));
  if (hasCoords(lg.delivery)) out.push({ lat: lg.delivery.lat, lng: lg.delivery.lng, label: letters[out.length], tone: "primary", title: lg.delivery.name || lg.delivery.address });
  return out;
};
